/* eslint-disable @typescript-eslint/no-empty-function */
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Body2, ButtonText } from 'stories/elements/Typography/Text'
import NewButton from 'components/elements/NewButton'
import SubjectBody from 'components/modules/Subjects/SubjectBody'
import { GroupedCategory, Subject, SubjectOption } from 'interfaces/Subject'
import theme from 'stories/utils/theme'
import { useToggleState } from 'lib/hooks/useToggleState'
import _useTranslation from 'lib/hooks/_useTranslation'
import styled, { css } from 'styled-components'
import SubjectSelectionDialog from './SubjectSelectionDialog'
import { toast } from 'react-toastify'
import GenericFormElement from 'components/modules/UploadDetails/GenericFormElement'
import { LanguageInterface } from 'interfaces/Language'
import Stack from 'components/elements/Stack'
import CollapsibleAccordion from 'components/modules/Accordion/CollapsibleAccordion'
import { ClientOnly } from 'components/elements/ClientOnly'
import Close from 'assets/icons/round_close.svg'
import SubjectService from 'services/SubjectService'
import { CountryCode } from 'interfaces/Country'
import { ApiError } from 'services/ApiService'
import {
    getGroupedSubjectCategories,
    getSubjectColorByCategory,
    getSubjectLanguageOptions,
    showSubjectGrid,
    showUncollapsedCategories,
    sortSubjects,
} from 'lib/formatting/subjects'
import CountryService from 'services/CountryService'
import { ExamType } from 'interfaces/ExamType'
import { getSelectableExamTypes } from 'lib/formatting/examType'
import { colorToCssFilter } from 'lib/features/color'

const DialogContentWrapper = styled.div`
    position: relative;
    display: grid;
    grid-template-rows: auto 1fr auto;
    gap: 0px;

    @media (max-width: 750px) {
        gap: 0px;
    }
`

const Text = styled(Body2)`
    padding-left: 10px;
`

const CategoryWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    height: 410px;
    overflow: auto;
    gap: 10px;
    place-content: start;
    width: 100%;
    padding: 15px 10px 5px 10px;
    gap: 10px;
    @media (max-height: 900px) or (max-width: 750px) {
        height: 315px;
    }
`

const Column = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-right: 7.5px;
    gap: 10px;
    @media (max-width: 750px) {
        padding: 0;
    }
`

const ButtonWrapper = styled.div<{ isDarkTheme?: boolean }>`
    display: grid;
    place-content: center;
    padding-top: 25px;
    width: 100%;
    background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.webappDialogBackground : theme.colors.white)};
    height: 40px;
`

const Hidden = styled.div`
    display: none;
`

export const ChipWrapper = styled.div<{ removePadding?: boolean }>`
    animation: show-chips 0.5s linear forwards;
    transition: all 0.3s ease-in-out;
    display: flex;
    align-items: center;
    position: relative;
    @keyframes show-chips {
        from {
            opacity: 0;
            bottom: 10px;
        }
        to {
            opacity: 1;
            bottom: 0px;
        }
    }
    display: grid;
    grid-template-columns: auto 1fr;

    gap: 10px;
    padding: ${({ removePadding }) => (removePadding ? 0 : '5px 10px')};

    .types {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
    }
    @media (max-width: 750px) {
        grid-template-columns: 1fr;
    }
`

export const ChipCard = styled.div<{ subjectColor?: string; isDarkTheme?: boolean }>`
    display: flex;
    align-items: center;
    gap: 5px;
    border-radius: ${theme.borderRadius.small};
    border: 2px solid ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.violetDarkerBackground)};
    padding: 0px 7.5px;
    cursor: pointer;
    width: fit-content;
    background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.violet)};
    ${({ subjectColor, isDarkTheme }) =>
        subjectColor
            ? css`
                  img {
                      filter: ${colorToCssFilter(subjectColor)};
                  }
                  p {
                      color: ${isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.subtitleBlack};
                  }
              `
            : ''}
`

interface Props {
    name: string
    label: string
    country: number | CountryCode
    initialSubject?: SubjectOption[]
    text?: string
    title?: string
    allowSubjectsFromDifferentLanguages?: boolean
    onSave?: (subjects: Subject[]) => void
    children?: ReactNode
    openByDefault?: boolean
    isDarkTheme?: boolean
}

const SubjectSelection = ({
    name,
    label,
    initialSubject,
    country,
    text,
    title,
    allowSubjectsFromDifferentLanguages,
    onSave,
    children,
    openByDefault,
    isDarkTheme,
}: Props) => {
    const [groupedCategories, setGroupedCategories] = useState<GroupedCategory[]>()
    const [selectedExamType, setSelectedExamType] = useState<ExamType | null | undefined>()
    const [examTypes, setExamTypes] = useState<ExamType[]>()
    const [defaultExamType, setDefaultExamType] = useState<ExamType | null>(null)
    const [showDialog, toggleShowDialog] = useToggleState(openByDefault ?? false)
    const [subjectsCount, setSubjectCount] = useState(0)
    const [selectedSubjects, setSelectedSubjects] = useState<Subject[]>(
        initialSubject?.length ? [...initialSubject.map((x) => x.data)] : []
    )

    const languageOptions = useMemo(() => getSubjectLanguageOptions(groupedCategories), [groupedCategories])
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('knows')

    const getInitialSelectedLanguage = () =>
        initialSubject?.length && initialSubject[0]?.data?.language
            ? initialSubject[0]?.data?.language
            : languageOptions[0]?.data

    const [selectedLanguage, setSelectedLanguage] = useState<LanguageInterface>()

    useEffect(() => {
        setSelectedLanguage(getInitialSelectedLanguage())
    }, [groupedCategories])

    const fetchExamTypes = useCallback(() => {
        CountryService.listExamTypes(country)
            .then((examTypes) => {
                const defaultExamType = examTypes?.length ? examTypes.find((exam) => exam.name === 'DEFAULT') : null
                defaultExamType && setDefaultExamType(defaultExamType)
                setSelectedExamType(defaultExamType)
                setExamTypes(getSelectableExamTypes(examTypes))
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'subjects.list')
                } else {
                    throw error
                }
            })
    }, [])

    useEffect(() => {
        fetchExamTypes()
    }, [fetchExamTypes])

    const fetchSubjects = useCallback(() => {
        if (selectedExamType === undefined) return
        SubjectService.list(country, selectedExamType)
            .then((subjects) => {
                const groupedCategories = getGroupedSubjectCategories(sortSubjects(subjects))
                setGroupedCategories(groupedCategories)
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'subjects.list')
                } else {
                    throw error
                }
            })
    }, [selectedExamType])

    useEffect(() => {
        fetchSubjects()
    }, [fetchSubjects])

    useEffect(() => {
        if (!groupedCategories) return
        let subjects: Subject[] = []
        groupedCategories.forEach((category) => {
            subjects = [...subjects, ...category.subjects.map((subject) => subject.data)]
        })
        if (!selectedLanguage) return setSubjectCount(subjects?.length ?? 0)
        setSubjectCount(subjects.filter(({ language }) => language.id === selectedLanguage.id)?.length ?? 0)
    }, [groupedCategories, selectedLanguage])

    if (!groupedCategories) return null

    const selectedPreview = selectedSubjects?.map((subject) => subject.name.trim()).join(', ') ?? ''

    const updateSelectedSubject = (subject: Subject) => {
        const wasSubjectAlreadyClicked = selectedSubjects.some((s) => s.id === subject.id)

        if (!allowSubjectsFromDifferentLanguages) {
            const doSubjectHaveDifferentLangauge =
                selectedSubjects?.length &&
                selectedSubjects[0]?.language?.id &&
                selectedSubjects[0].language.id !== subject?.language?.id

            if (doSubjectHaveDifferentLangauge) {
                toast.warn(t('knows/createDetailsSubjectsDifferentLanguagesError'))
                return
            }
            if (!wasSubjectAlreadyClicked && selectedSubjects.length > 1) {
                toast.warn(t('knows/createDetailsPageMaxSubjectsError'))
                return
            }
        }

        setSelectedSubjects((subjects) =>
            wasSubjectAlreadyClicked ? subjects.filter((s) => s.id !== subject.id) : [...subjects, subject]
        )
    }

    const onLanguageSelected = (language: LanguageInterface) => setSelectedLanguage(language)

    const filterCategories = (options: GroupedCategory[]) =>
        selectedLanguage?.id
            ? options
                  .filter((option) => option.languages.map(({ id }) => id).includes(selectedLanguage.id))
                  .map((option) => {
                      const { subjects, ...rest } = option
                      const filteredSubjects = subjects.filter(
                          (subject) => subject.data.language.name === selectedLanguage.name
                      )
                      return { subjects: filteredSubjects, ...rest }
                  })
            : options

    const changeExamType = (examType: ExamType | null) => setSelectedExamType(examType)

    const hasExamBoards = examTypes?.length

    const showLanguageDropdown = languageOptions?.length > 1

    const isExamTypeSelected = selectedExamType && selectedExamType.name !== 'DEFAULT'

    const isLanguageSelected = (language: LanguageInterface) => selectedLanguage && selectedLanguage.id === language.id

    const onSaveClicked = () => {
        onSave && onSave(selectedSubjects)
        toggleShowDialog()
    }

    const disableClosingSubjectSelection = !!openByDefault && !initialSubject?.length

    return (
        <>
            <ClientOnly>
                <SubjectSelectionDialog
                    open={showDialog}
                    onClose={toggleShowDialog}
                    title={title ?? t('knows/createDetailsMultipleSubjectCategories')}
                    ariaPrefix=""
                    disableBackdropClick={disableClosingSubjectSelection}
                    hideCloseButton={disableClosingSubjectSelection}
                    isDarkTheme={isDarkTheme}
                    backgroundColor={isDarkTheme ? theme.colors.webappDialogBackground : theme.colors.white}
                    headerColor={isDarkTheme ? theme.colors.webappDialogBackground : theme.colors.white}
                >
                    <Stack gutter={0}>
                        {text ? (
                            <Text
                                fontFamily="var(--font-inter)"
                                fontWeight={400}
                                color={theme.colors.actionGray}
                                marginBottom={10}
                            >
                                {text}
                            </Text>
                        ) : null}
                        <DialogContentWrapper>
                            <Stack gutter={0}>
                                {showLanguageDropdown ? (
                                    <>
                                        <ChipWrapper>
                                            <ButtonText
                                                fontWeight={400}
                                                color={
                                                    isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.body2Black
                                                }
                                                fontFamily="var(--font-inter)"
                                            >
                                                {t('knows/createDetailsPageKnowLanguageLabel')}
                                            </ButtonText>
                                            <div className="types">
                                                {languageOptions?.map(({ data, label }) => (
                                                    <ChipCard
                                                        key={data.id}
                                                        onClick={() => onLanguageSelected(data)}
                                                        subjectColor={
                                                            isLanguageSelected(data)
                                                                ? theme.colors.deepSeaBlue
                                                                : theme.colors.placeholderText
                                                        }
                                                    >
                                                        <Body2
                                                            color={theme.colors.deepSeaBlue}
                                                            fontFamily="var(--font-inter)"
                                                            fontWeight={400}
                                                        >
                                                            {label}
                                                        </Body2>
                                                    </ChipCard>
                                                ))}
                                            </div>
                                        </ChipWrapper>
                                    </>
                                ) : null}
                                {hasExamBoards ? (
                                    <ChipWrapper>
                                        {isExamTypeSelected ? (
                                            <>
                                                <ButtonText
                                                    fontWeight={400}
                                                    color={
                                                        isDarkTheme
                                                            ? theme.colors.grayScaleSecondary
                                                            : theme.colors.body2Black
                                                    }
                                                    fontFamily="var(--font-inter)"
                                                >
                                                    {t('knows/selectedExamTypeLabel')}
                                                </ButtonText>
                                                <ChipCard onClick={() => changeExamType(defaultExamType)}>
                                                    <Body2
                                                        color={theme.colors.deepSeaBlue}
                                                        fontFamily="var(--font-inter)"
                                                        fontWeight={400}
                                                    >
                                                        {selectedExamType.name}
                                                    </Body2>
                                                    <Close />
                                                </ChipCard>
                                            </>
                                        ) : (
                                            <>
                                                <ButtonText
                                                    fontWeight={400}
                                                    color={
                                                        isDarkTheme
                                                            ? theme.colors.grayScaleSecondary
                                                            : theme.colors.body2Black
                                                    }
                                                    fontFamily="var(--font-inter)"
                                                >
                                                    {t('knows/selectedExamTypeFilterLabel')}
                                                </ButtonText>
                                                <div className="types">
                                                    {examTypes.map((exam) => (
                                                        <ChipCard key={exam.uuid} onClick={() => changeExamType(exam)}>
                                                            <Body2
                                                                color={theme.colors.subtitleBlack}
                                                                fontFamily="var(--font-inter)"
                                                                fontWeight={400}
                                                            >
                                                                {exam.name}
                                                            </Body2>
                                                        </ChipCard>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </ChipWrapper>
                                ) : null}
                                {selectedSubjects?.length ? (
                                    <ChipWrapper>
                                        <ButtonText
                                            fontFamily="var(--font-inter)"
                                            fontWeight={400}
                                            color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.body2Black}
                                        >
                                            {t('knows/selectedSubjectsLabel')}
                                        </ButtonText>
                                        <div className="types">
                                            {selectedSubjects?.map((subject) => (
                                                <ChipCard
                                                    key={subject.id}
                                                    onClick={() => updateSelectedSubject(subject)}
                                                    subjectColor={getSubjectColorByCategory(subject.colorCategory)}
                                                    isDarkTheme={isDarkTheme}
                                                >
                                                    <img src={subject.iconUrl} alt="-" width={18} height={18} />
                                                    <Body2
                                                        color={theme.colors.deepSeaBlue}
                                                        fontFamily="var(--font-inter)"
                                                        fontWeight={400}
                                                    >
                                                        {subject.name}
                                                    </Body2>
                                                    <Close />
                                                </ChipCard>
                                            ))}
                                        </div>
                                    </ChipWrapper>
                                ) : null}
                            </Stack>

                            <CategoryWrapper>
                                {showSubjectGrid(subjectsCount) ? (
                                    <Column>
                                        {filterCategories(groupedCategories).map(({ subjects }) =>
                                            subjects.map(({ data: subject }) => (
                                                <span key={subject.id} onClick={() => updateSelectedSubject(subject)}>
                                                    <SubjectBody
                                                        subject={subject}
                                                        isSelected={selectedSubjects.some((s) => s.id === subject.id)}
                                                        isDarkTheme={isDarkTheme}
                                                    />
                                                </span>
                                            ))
                                        )}
                                    </Column>
                                ) : (
                                    <>
                                        {filterCategories(groupedCategories).map(({ category, subjects }) => (
                                            <>
                                                <CollapsibleAccordion
                                                    key={category}
                                                    title={category}
                                                    hideBorder
                                                    noPadding
                                                    noMargin
                                                    isDarkTheme={isDarkTheme}
                                                    openByDefault={showUncollapsedCategories(subjectsCount)}
                                                    text={
                                                        <Column>
                                                            {subjects.map(({ data: subject }) => (
                                                                <span
                                                                    key={subject.id}
                                                                    onClick={() => updateSelectedSubject(subject)}
                                                                >
                                                                    <SubjectBody
                                                                        subject={subject}
                                                                        isSelected={selectedSubjects.some(
                                                                            (s) => s.id === subject.id
                                                                        )}
                                                                        isDarkTheme={isDarkTheme}
                                                                    />
                                                                </span>
                                                            ))}
                                                        </Column>
                                                    }
                                                />
                                            </>
                                        ))}
                                    </>
                                )}
                            </CategoryWrapper>
                            <ButtonWrapper isDarkTheme={isDarkTheme}>
                                <NewButton
                                    onClick={onSaveClicked}
                                    disableFullWidth
                                    disabled={!selectedSubjects?.length}
                                    width={250}
                                    height={50}
                                    color={
                                        !selectedSubjects?.length ? theme.colors.placeholderText : theme.colors.deepSeaBlue
                                    }
                                    borderRadius={theme.borderRadius.normal}
                                    isWebapp={isDarkTheme}
                                >
                                    <ButtonText
                                        color={theme.colors.white}
                                        fontWeight={500}
                                        fontSize={14}
                                        fontFamily="var(--font-open-sans)"
                                    >
                                        {tCommon('common/save')}
                                    </ButtonText>
                                </NewButton>
                            </ButtonWrapper>
                        </DialogContentWrapper>
                    </Stack>
                </SubjectSelectionDialog>
            </ClientOnly>
            {children ? (
                <>
                    <div onClick={toggleShowDialog}>{children}</div>
                    <Hidden>
                        <GenericFormElement
                            data-id="subjects"
                            name={name}
                            onClick={toggleShowDialog}
                            label={label}
                            placeholder={label}
                            value={selectedSubjects}
                            selectedPreview={selectedPreview}
                            // eslint-disable-next-line @typescript-eslint/no-empty-function
                            onUpdate={() => {}}
                            isDarkTheme={isDarkTheme}
                        />
                    </Hidden>
                </>
            ) : (
                <GenericFormElement
                    data-id="subjects"
                    name={name}
                    onClick={toggleShowDialog}
                    label={label}
                    placeholder={label}
                    value={selectedSubjects}
                    selectedPreview={selectedPreview}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onUpdate={() => {}}
                    isDarkTheme={isDarkTheme}
                />
            )}
        </>
    )
}

export default SubjectSelection
